<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleBlock from './title-block.vue';
import ImagesWithStripes from './images-with-stripes.vue';
import StarsRateSvg from './stars-rate-svg.vue';

const b = bem('ready-order-block');

export default {
  mixins: [publishGuardMixin],
  name: 'ReadyOrderBlock',
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    TitleBlock,
    ImagesWithStripes,
    StarsRateSvg,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      titleModalVisible: false,
      bottomTextModalVisible: false,
      bottomTextBoldModalVisible: false,
      phoneModalVisible: false,
      descriptionModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    // Title
    title() {
      return this.data?.title || 'Ready to order?';
    },
    titleMobile() {
      return this.data?.titleMobile || this.title;
    },
    displayTitle() {
      return this.isMobileView ? this.titleMobile : this.title;
    },
    // Images
    leftImage() {
      return this.data?.leftImage || '';
    },
    leftImageMobile() {
      return this.data?.leftImageMobile || '';
    },
    rightImage() {
      return this.data?.rightImage || '';
    },
    rightImageMobile() {
      return this.data?.rightImageMobile || '';
    },
    leftStripes() {
      return this.data?.leftStripes || [
        {
          id: 1, text: '', color: '#293241', colorText: '#ECC486',
        },
        {
          id: 2, text: 'place an order online', color: '#ECC486', colorText: '#293241',
        },
      ];
    },
    rightStripes() {
      return this.data?.rightStripes || [
        {
          id: 1, text: '', color: '#D7E8F0', colorText: '#5F8AA4',
        },
        {
          id: 2, text: 'talk to our team', color: '#5F8AA4', colorText: '#fff',
        },
      ];
    },
    isPod() {
      return this.data?.isPod || false;
    },
    // Bottom block mode: 'phone' or 'description'
    bottomMode() {
      return this.data?.bottomMode || 'phone';
    },
    isPhoneMode() {
      return this.bottomMode === 'phone';
    },
    // Bottom block - phone mode (two parts: regular + bold)
    bottomText() {
      return this.data?.bottomText || 'TALK TO A';
    },
    bottomTextMobile() {
      return this.data?.bottomTextMobile || this.bottomText;
    },
    displayBottomText() {
      return this.isMobileView ? this.bottomTextMobile : this.bottomText;
    },
    bottomTextBold() {
      return this.data?.bottomTextBold || 'REAL PERSON!';
    },
    bottomTextBoldMobile() {
      return this.data?.bottomTextBoldMobile || this.bottomTextBold;
    },
    displayBottomTextBold() {
      return this.isMobileView ? this.bottomTextBoldMobile : this.bottomTextBold;
    },
    phoneNumber() {
      return this.data?.phoneNumber || '800-916-6076';
    },
    availability() {
      return 'AVAILABLE FROM: M-F 9-6, SAT 10-5 EST';
    },
    // Bottom block - description mode
    bottomDescription() {
      return this.data?.bottomDescription || '';
    },
    bottomDescriptionMobile() {
      return this.data?.bottomDescriptionMobile || this.bottomDescription;
    },
    displayBottomDescription() {
      return this.isMobileView ? this.bottomDescriptionMobile : this.bottomDescription;
    },
    reviewsCount() {
      return this.data?.reviewsCount || 2717;
    },
  },
  methods: {
    // Title modal
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    handleTitleSave(values) {
      this.$emit('update', {
        title: values.title,
        titleMobile: values.titleMobile,
      });
      this.titleModalVisible = false;
    },
    // Bottom text modal
    openBottomTextModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.bottomTextModalVisible = true;
      }
    },
    handleBottomTextSave(values) {
      this.$emit('update', {
        bottomText: values.description,
        bottomTextMobile: values.descriptionMobile,
      });
      this.bottomTextModalVisible = false;
    },
    // Bottom text bold modal
    openBottomTextBoldModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.bottomTextBoldModalVisible = true;
      }
    },
    handleBottomTextBoldSave(values) {
      this.$emit('update', {
        bottomTextBold: values.description,
        bottomTextBoldMobile: values.descriptionMobile,
      });
      this.bottomTextBoldModalVisible = false;
    },
    // Phone modal
    openPhoneModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.phoneModalVisible = true;
      }
    },
    handlePhoneSave(values) {
      this.$emit('update', {
        phoneNumber: values.description,
      });
      this.phoneModalVisible = false;
    },
    // Description modal (for description mode)
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        bottomDescription: values.description,
        bottomDescriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    // Toggle bottom mode
    toggleBottomMode() {
      this.$emit('update', {
        bottomMode: this.isPhoneMode ? 'description' : 'phone',
      });
    },
    // Images update
    handleImagesUpdate(updates) {
      this.$emit('update', updates);
    },
  },
};
</script>

<template>
  <div :class="b('', { mix: className, pod: isPod, isMobileView })">
    <!-- Title -->
    <div
      v-if="!isPod"
      :class="b('title-wrap', { editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openTitleModal"
      @keyup.enter="openTitleModal"
    >
      <TitleBlock
        :class="b('title', { isMobileView })"
        :is-mobile-view="isMobileView"
        :is-white="true"
      >
        {{ displayTitle }}
      </TitleBlock>
      <PencilIcon
        v-if="isEditMode"
        :class="b('edit-icon')"
        color="#fff"
        :size="20"
      />
    </div>

    <!-- Pod Title -->
    <div
      v-else
      :class="b('title-pod-wrap', { editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openTitleModal"
      @keyup.enter="openTitleModal"
    >
      <TitleBlock
        :class="b('title-pod', { isMobileView })"
        :is-mobile-view="isMobileView"
        :is-white="true"
      >
        {{ displayTitle }}
      </TitleBlock>
      <PencilIcon
        v-if="isEditMode"
        :class="b('edit-icon')"
        color="#fff"
        :size="20"
      />
    </div>

    <!-- Images with stripes -->
    <ImagesWithStripes
      :class="b('images-list', { pod: isPod, isMobileView })"
      :left-image="leftImage"
      :left-image-mobile="leftImageMobile"
      :right-image="rightImage"
      :right-image-mobile="rightImageMobile"
      :left-stripes="leftStripes"
      :right-stripes="rightStripes"
      :is-pod="isPod"
      :is-mobile-view="isMobileView"
      :is-edit-mode="isEditMode"
      @update="handleImagesUpdate"
    />

    <!-- Toggle for bottom mode (edit mode only) -->
    <div v-if="isEditMode && !isPod" :class="b('toggle-controls')">
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Bottom Mode:</span>
        <a-radio-group
          :value="bottomMode"
          @change="(e) => $emit('update', { bottomMode: e.target.value })"
        >
          <a-radio-button value="phone">Phone</a-radio-button>
          <a-radio-button value="description">Description</a-radio-button>
        </a-radio-group>
      </div>
    </div>

    <!-- Bottom block (only for non-pod) -->
    <div v-if="!isPod" :class="b('bottom-block', { isMobileView })">
      <!-- Bottom text (two parts: regular + bold) -->
      <div :class="b('bottom-text-row', { isMobileView })"><span
          :class="b('bottom-text', { isMobileView, editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openBottomTextModal"
          @keyup.enter="openBottomTextModal"
        >{{ displayBottomText }}<PencilIcon
            v-if="isEditMode"
            :class="b('edit-icon', { small: true, inline: true })"
            color="#293241"
            :size="14"
          /></span> <span
          :class="b('bottom-text', { isMobileView, bold: true, editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openBottomTextBoldModal"
          @keyup.enter="openBottomTextBoldModal"
        >{{ displayBottomTextBold }}<PencilIcon
            v-if="isEditMode"
            :class="b('edit-icon', { small: true, inline: true })"
            color="#293241"
            :size="14"
          /></span></div>
      <!-- Phone mode -->
      <template v-if="isPhoneMode">

        <!-- Phone -->
        <div
          :class="b('phone-wrap', { editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openPhoneModal"
          @keyup.enter="openPhoneModal"
        >
          <a :class="b('phone', { isMobileView })" :href="`tel:${phoneNumber}`">
            {{ phoneNumber }}
          </a>
          <PencilIcon
            v-if="isEditMode"
            :class="b('edit-icon', { small: true })"
            color="#ECC486"
            :size="16"
          />
        </div>

        <!-- Availability (hardcoded) -->
        <span :class="b('availability', { isMobileView })">
          {{ availability }}
        </span>
      </template>

      <!-- Description mode -->
      <template v-else>
        <!-- Description text -->
        <div
          v-if="bottomDescription || isEditMode"
          :class="b('description-wrap', { editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openDescriptionModal"
          @keyup.enter="openDescriptionModal"
        >
          <p v-if="displayBottomDescription" :class="b('description', { isMobileView })">
            {{ displayBottomDescription }}
          </p>
          <span v-else :class="b('description-placeholder')">
            + Add Description
          </span>
          <PencilIcon
            v-if="isEditMode && displayBottomDescription"
            :class="b('edit-icon', { small: true })"
            color="#293241"
            :size="16"
          />
        </div>
      </template>

      <!-- Line block (hardcoded) -->
      <div :class="b('line-block', { isMobileView })" />

      <!-- Rate (hardcoded) -->
      <div :class="b('rate', { isMobileView })">
        <StarsRateSvg
          :class="b('stars')"
          :width="isMobileView ? 140 : 224"
          :height="isMobileView ? 24 : 38"
          fill="#eec586"
          stroke="#eec586"
        />
        <span :class="b('rate-count', { isMobileView })">
          ({{ reviewsCount }})
        </span>
        <span :class="b('rate-link', { isMobileView })">SEE ALL REVIEWS</span>
      </div>
    </div>

    <!-- Modals -->
    <EditTitleModal
      :visible="titleModalVisible"
      :title="title"
      :title-mobile="titleMobile"
      modal-title="Edit Block Title"
      @close="titleModalVisible = false"
      @save="handleTitleSave"
    />

    <EditDescriptionModal
      :visible="bottomTextModalVisible"
      :description="bottomText"
      :description-mobile="bottomTextMobile"
      title="Edit Bottom Text (Part 1)"
      @close="bottomTextModalVisible = false"
      @save="handleBottomTextSave"
    />

    <EditDescriptionModal
      :visible="bottomTextBoldModalVisible"
      :description="bottomTextBold"
      :description-mobile="bottomTextBoldMobile"
      title="Edit Bottom Text Bold (Part 2)"
      @close="bottomTextBoldModalVisible = false"
      @save="handleBottomTextBoldSave"
    />

    <EditDescriptionModal
      :visible="phoneModalVisible"
      :description="phoneNumber"
      :show-mobile="false"
      title="Edit Phone Number"
      @close="phoneModalVisible = false"
      @save="handlePhoneSave"
    />

    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="bottomDescription"
      :description-mobile="bottomDescriptionMobile"
      title="Edit Bottom Description"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.ready-order-block {
  width: 100%;
  padding: 0 0 60px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @include mediaMinMax(1025, $normal) {
    padding: 0 0 vw-full(60) 0;
  }

  @include media($small) {
    padding: 0 0 vw-mobile(43, 412) 0;
  }

  &_isMobileView {
    padding: 0 0 43px 0;
  }

  &_pod {
    padding: 64px 0;
    background: #9e9e9e;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(64) 0;
    }

    @include media($small) {
      padding: vw-mobile(40) 0 vw-mobile(75);
    }

    &.ready-order-block_isMobileView {
      padding: 40px 0 75px;
    }
  }

  &__edit-icon {
    flex-shrink: 0;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &_small {
      margin-left: 8px;
    }

    &_inline {
      display: inline;
      vertical-align: super;
      margin-left: 4px;
    }
  }

  &__title-wrap {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background-color: #293241;
    min-height: 153px;
    padding: 40px 0;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      min-height: vw-full(153);
      padding: vw-full(40) 0;
      gap: vw-full(12);
    }

    @include media($small) {
      min-height: vw-mobile(95, 412);
      height: fit-content;
      padding: vw-mobile(28) 0;
    }

    &_editable {
      cursor: pointer;
      transition: background 0.2s ease;

      &:hover {
        background: lighten(#293241, 5%);

        .ready-order-block__edit-icon {
          opacity: 1;
        }
      }
    }
  }

  &__title {
    color: #fff !important;
    text-transform: uppercase !important;
    font-size: 60px !important;
    font-weight: 600 !important;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(60) !important;
    }

    @include media($small) {
      font-size: vw-mobile(32, 412) !important;
    }

    &_isMobileView {
      font-size: 32px !important;
    }
  }

  &__title-pod-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 10px 20px;
    border-radius: 8px;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      padding: vw-full(10) vw-full(20);
      border-radius: vw-full(8);
    }

    &_editable {
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.1);

        .ready-order-block__edit-icon {
          opacity: 1;
        }
      }
    }
  }

  &__title-pod {
    color: #fff !important;
  }

  &__images-list {
    margin: 64px 0;

    @include mediaMinMax(1025, $normal) {
      margin: vw-full(64) 0;
    }

    @include media($small) {
      margin: vw-mobile(28, 412) 0;
    }

    &_isMobileView {
      margin: 28px 0;
    }

    &_pod {
      margin-top: 32px;

      @include mediaMinMax(1025, $normal) {
        margin-top: vw-full(32);
      }

      @include media($small) {
        margin-top: vw-mobile(28, 412);
      }

      &.ready-order-block__images-list_isMobileView {
        margin-top: 28px;
      }
    }
  }

  &__bottom-block {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    margin-top: 40px;

    @include mediaMinMax(1025, $normal) {
      margin-top: vw-full(40);
      gap: vw-full(32);
    }

    &_isMobileView {
      margin-top: 28px;
      gap: 16px;
    }
  }

  &__bottom-text-row {
    display: block;
    text-align: center;

    @include media($small) {
      padding: 0 vw-mobile(28);
    }

    &_isMobileView {
      padding: 0 28px;
    }
  }

  &__bottom-text {
    color: #293241;
    font-size: 40px;
    font-weight: 400;
    text-transform: uppercase;
    font-family: Montserrat, sans-serif;
    display: inline;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    @include media($small) {
      font-size: vw-mobile(24, 412);
    }

    &_isMobileView {
      font-size: 24px;
    }

    &_bold {
      font-weight: 500;
      margin-left: 8px;
    }

    &_editable {
      cursor: pointer;
      border-radius: 4px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .ready-order-block__edit-icon {
          opacity: 1;
        }
      }
    }
  }

  &__phone-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    border-radius: 8px;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(8) vw-full(16);
      border-radius: vw-full(8);
    }

    &_editable {
      cursor: pointer;

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .ready-order-block__edit-icon {
          opacity: 1;
        }
      }
    }
  }

  &__phone {
    color: #ECC486;
    font-size: 40px;
    font-weight: 500;
    text-decoration: underline;
    text-transform: uppercase;
    line-height: normal;
    font-family: Montserrat, sans-serif;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    @include media($small) {
      font-size: vw-mobile(24, 412);
      color: #293241;
    }

    &_isMobileView {
      font-size: 24px;
      color: #293241;
    }
  }

  &__availability {
    color: #293241;
    font-size: 28px;
    font-weight: 400;
    text-transform: uppercase;
    font-family: Montserrat, sans-serif;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
    }

    @include media($small) {
      font-size: vw-mobile(16, 412);
    }

    &_isMobileView {
      font-size: 16px;
    }
  }

  &__line-block {
    width: 242px;
    height: 2px;
    background-color: #293241;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(242);
    }

    @include media($small) {
      width: vw-mobile(140, 412);
      height: 1px;
    }

    &_isMobileView {
      width: 140px;
      height: 1px;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: -4px;
      width: 10px;
      height: 10px;
      background-color: #293241;
      transform: rotate(45deg);

      @include mediaMinMax(1025, $normal) {
        width: vw-full(10);
        height: vw-full(10);
        top: vw-full(-4);
      }

      @include media($small) {
        display: none;
      }
    }

    &_isMobileView::before,
    &_isMobileView::after {
      display: none;
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }
  }

  &__rate {
    display: flex;
    align-items: center;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }

    &_isMobileView {
      gap: 8px;
    }
  }

  &__rate-count {
    color: #293241;
    font-size: 28px;
    font-weight: 400;
    font-family: Montserrat, sans-serif;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
    }

    @include media($small) {
      font-size: vw-mobile(14, 412);
    }

    &_isMobileView {
      font-size: 14px;
    }
  }

  &__rate-link {
    color: #eec586;
    font-size: 28px;
    font-weight: 500;
    text-decoration: underline;
    font-family: Montserrat, sans-serif;
    padding-left: 20px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(28);
      padding-left: vw-full(20);
    }

    &_isMobileView {
      font-size: 14px;
    }
  }

  &__toggle-controls {
    display: flex;
    justify-content: center;
    gap: 24px;
    padding: 16px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(24);
      padding: vw-full(16);
      border-radius: vw-full(8);
    }
  }

  &__toggle-item {
    display: flex;
    align-items: center;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }

  &__toggle-label {
    font-size: 14px;
    font-weight: 500;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
    }
  }

  &__description-wrap {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 16px 24px;
    border-radius: 8px;
    transition: background 0.2s ease;
    max-width: 1200px;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(16) vw-full(24);
      border-radius: vw-full(8);
      max-width: vw-full(1200);
    }

    @include media($small) {
      padding: vw-mobile(12, 412) vw-mobile(16, 412);
    }

    &_editable {
      cursor: pointer;

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .ready-order-block__edit-icon {
          opacity: 1;
        }
      }
    }
  }

  &__description {
    color: #293241;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.5;
    margin: 0;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }

    @include media($small) {
      font-size: vw-mobile(16, 412);
    }

    &_isMobileView {
      font-size: 16px;
    }
  }

  &__description-placeholder {
    color: #7f7f7f;
    font-size: 18px;
    font-weight: 400;
    cursor: pointer;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(18);
    }

    &:hover {
      color: #293241;
    }
  }
}
</style>
